import { syncedInfoWrapped } from 'common/components/Autosync/SyncedInfo.tsx';
import { junctionColorMapping } from 'common/helpers/junctionColorMapping';
import { stringifyUnableToLoadContent } from 'features/analyses/common/UnableToLoadContent/UnableToLoadContent';
import { isSVGminitableIngestionEnabled } from 'common/helpers/featureFlags';
import { onSyncedInfoEventListener } from '../../helpers/insertMinitableEventListener';
import showAutosync from '../../helpers/ShowAutosync.ts';

const isManualRefreshEnabled = localStorage.getItem('isManualRefreshEnabled');
const AUTOSYNC_TEXT = 'Autosynced from';

const getFilenameFromPath = (input) =>
  input.substring(input.lastIndexOf('/') + 1);

const getFilename = (input) => {
  if (input?.startsWith('http')) {
    const urlParsed = new URL(input);
    return decodeURI(getFilenameFromPath(urlParsed.pathname));
  }

  return input;
};

export default async function initializeCKEditor({
  projectId,
  editor,
  richTextData,
  childrenData,
  isReadOnly = true,
  skipAutoSyncOnValidData = false,
  contextStatusInfoItem,
  callBack = () => {},
  isPwcUser,
}) {
  editor.disableReadOnlyMode(projectId);

  const showAutoSync = await showAutosync(projectId, isPwcUser);

  childrenData.forEach((item) => {
    const {
      currentFormatType,
      data = {},
      error,
      fileName: fileNameItem,
      htmlData,
      id,
      notFound,
      renderedHtml,
      renderedSvg,
      request = {},
      tempRenderSvg,
      type,
      isShared,
    } = item;
    const fileName = fileNameItem || getFilename(request.sharePointItemUrl);
    const { groupName, slideNumber, autoSync, formatType } = data;
    const sharePointItemUrl =
      data.sharePointItemUrl || request.sharePointItemUrl;
    const worksheetName = data.worksheetName || request.worksheetName;

    const excelPPTItem = {
      autoSync,
      error,
      fileName,
      formatType,
      groupName,
      libraryItemId: id,
      sharePointItemUrl,
      slideNumber,
      sourceUrl: sharePointItemUrl,
      type,
      worksheetName,
    };

    // Added code logic to support initialize RTE for shared Content.
    const canLoadData = (isShared ? data : htmlData) && !notFound;
    const bypassAutoSync = skipAutoSyncOnValidData && !notFound;

    const syncedInfoComponent =
      (showAutoSync && !bypassAutoSync && isManualRefreshEnabled === 'false') ||
      (!!contextStatusInfoItem.isEditMode && isManualRefreshEnabled === 'false')
        ? syncedInfoWrapped({
            projectId,
            excelPPTItem,
            contextStatusInfoItem,
            svgMinitableShow: isSVGminitableIngestionEnabled,
            isPwcUser,
          })
        : '';
    // Added code logic to support initialize RTE for shared Content.
    const renderComponent =
      (currentFormatType || formatType) === 'SVG'
        ? tempRenderSvg || renderedSvg || item.rendered
        : junctionColorMapping(
            renderedHtml || htmlData || item.rendered || data
          );
    const autosyncExist = renderComponent.includes(AUTOSYNC_TEXT);
    const renderWithAutosync = autosyncExist
      ? renderComponent
      : `${renderComponent}${syncedInfoComponent}`;
    const unableToLoadComponent = stringifyUnableToLoadContent({
      type,
      data: {
        ...excelPPTItem,
        ...data,
      },
      apiErrorCode: error?.apiErrorCode,
      errorCode: error?.errorCode || error?.apiErrorCode,
      isPwcUser,
    });
    const rendered = canLoadData
      ? `<div>${renderWithAutosync}</div>`
      : `<div>${unableToLoadComponent}</div>`;

    // eslint-disable-next-line no-underscore-dangle
    const oldItemIfExists = window.__LIBRARYITEMSTORE__.get(id) || {};
    const mappedItem = {
      ...oldItemIfExists,
      id,
      rendered,
    };
    // eslint-disable-next-line no-underscore-dangle
    window.__LIBRARYITEMSTORE__.add(mappedItem);
    if (isSVGminitableIngestionEnabled) {
      document.body.addEventListener(
        'click',
        onSyncedInfoEventListener({
          canShowAutoSync: showAutoSync,
          contextStatusInfoItem,
          data,
          editor,
          excelPPTItem,
          id,
          libraryItem: mappedItem,
          projectId,
          renderedHtml: junctionColorMapping(mappedItem.renderedHtml),
          renderedSvg: mappedItem.tempRenderSvg || mappedItem.renderedSvg,
          isPwcUser,
        })
      );
    }
  });

  editor.setData(richTextData);

  if (isReadOnly) {
    editor.enableReadOnlyMode(projectId);
  }

  callBack();
}
